import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import CreditCard from 'components/FrictionlessCheckout/Payment/CreditCard';
import Paypal from 'components/FrictionlessCheckout/Payment/PaymentMethodList/Paypal';
import UseAGiftCard from 'components/FrictionlessCheckout/PaymentReleaseFour/GiftCards/UseAGiftCard';
import GiftCardItem from 'components/FrictionlessCheckout/Payment/GiftCards/GiftCardItem';
import { colors, fontSizes } from 'style/config';
import { PAYMENT_LOGO_SIZE } from 'constants/Checkout';
import ErrorMsg from 'components/ErrorMsg';
import {
    Box, Divider, Text, Grid, Image
} from 'components/ui';
import dateUtils from 'utils/Date';
import PaymentDetails from 'components/FrictionlessCheckout/Payment/PaymentDetails';
import AlternatePaymentButtons from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/AlternatePaymentButtons/AlternatePaymentButtons';
import { PAYMENT_TYPES } from 'constants/RwdBasket';

const getExpDateView = expirationDate => {
    const expDateObj = dateUtils.getDateObjectFromString(expirationDate);

    return expDateObj ? dateUtils.getDateInMMYYYY(expDateObj) : null;
};

// eslint-disable-next-line complexity
const PaymentsDisplay = ({
    isPaymentComplete,
    creditCardPaymentGroup,
    localization,
    isPaypalSelected,
    payPalPaymentGroup,
    venmoUsername,
    storeCredits,
    giftCardPaymentGroups,
    hasGiftCardInOrder,
    alternateMethodSelected,
    alternatePaymentName,
    checkoutEnabled,
    installmentValue,
    showError,
    isEfullfillmentOrder,
    shouldRenderGiftCardSection,
    isAfterpayEnabledForThisOrder,
    isKlarnaEnabledForThisOrder,
    isPayPalEnabled,
    isPayPalPayLaterEligible,
    isAfterpayEnabledForThisProfile,
    handleAfterPayExpand,
    handleKlarnaExpand,
    handlePayPalClick,
    handlePazeExpand
}) => {
    // Check if a standard payment is complete (credit card, PayPal)
    const hasStandardPaymentComplete = (creditCardPaymentGroup?.isComplete || isPaypalSelected) && isPaymentComplete;
    // Alternate payment methods (Afterpay, Klarna, Venmo, Paze) can be shown when selected
    // even before the backend order is updated, as they complete during the checkout redirect
    const hasAlternatePaymentSelected = alternateMethodSelected;
    const hasNonGiftCardNonCreditPaymentAdded = hasStandardPaymentComplete || hasAlternatePaymentSelected;
    const hasNonGiftCardPaymentAdded = hasNonGiftCardNonCreditPaymentAdded || storeCredits.length > 0;
    const shouldShowPazeGiftCardMessage = alternatePaymentName === PAYMENT_TYPES.PAY_PAZE && alternateMethodSelected;
    const shouldShowAlternatePaymentButtons =
        alternatePaymentName !== PAYMENT_TYPES.PAY_AFTERPAY && alternatePaymentName !== PAYMENT_TYPES.PAY_KLARNA;

    const handleEditAlternatePayment = () => {
        const handlers = {
            [PAYMENT_TYPES.PAY_KLARNA]: handleKlarnaExpand,
            [PAYMENT_TYPES.PAY_AFTERPAY]: handleAfterPayExpand,
            [PAYMENT_TYPES.PAY_PAZE]: handlePazeExpand
        };

        const handler = handlers[alternatePaymentName];
        handler && handler();
    };

    return (
        <>
            <Box paddingX={[4, 5]}>
                {/* Standard payment methods (Credit Card, PayPal) */}
                {!alternateMethodSelected && (
                    <>
                        {isPaymentComplete && showError && (
                            <ErrorMsg
                                children={localization.verifyCVV}
                                fontSize={fontSizes.base}
                            />
                        )}
                        {creditCardPaymentGroup && <CreditCard creditCard={creditCardPaymentGroup} />}
                        {isPaypalSelected && <Paypal payPalPaymentGroup={payPalPaymentGroup} />}
                    </>
                )}
                {alternateMethodSelected && (
                    <Grid
                        columns='1fr auto'
                        gap={3}
                        alignItems='flex-start'
                    >
                        <PaymentDetails
                            defaultPayment
                            paymentName={alternatePaymentName}
                            selected
                            checkoutEnabled={checkoutEnabled}
                            installmentValue={installmentValue}
                            isSaveState
                            username={venmoUsername}
                        />
                        <Text
                            fontSize='base'
                            fontWeight='normal'
                            textDecoration='underline'
                            color='blue'
                            css={{ cursor: 'pointer' }}
                            onClick={handleEditAlternatePayment}
                            data-at={Sephora.debug.dataAt('edit_alternate_payment')}
                        >
                            {localization.edit}
                        </Text>
                    </Grid>
                )}
                {storeCredits?.map((storeCredit, index) => (
                    <>
                        {(hasNonGiftCardNonCreditPaymentAdded || index !== 0) && (
                            <Divider
                                marginTop={[3, 4]}
                                marginBottom={[3, 4]}
                                color={colors.lightGray}
                                width='100%'
                                marginX={[-4, -5]}
                            />
                        )}
                        <Grid
                            key={`storeCredits_${index.toString()}`}
                            columns='auto 1fr'
                            gap={3}
                            alignItems='center'
                            lineHeight='tight'
                            marginTop={[3, 4]}
                        >
                            <Image
                                display='block'
                                src={'/img/ufe/payments/accountCredit.svg'}
                                alt={localization.accountCredit}
                                {...PAYMENT_LOGO_SIZE}
                            />
                            <Text>
                                {`+ ${storeCredit.amount} ${localization.accountCredit}`}
                                {storeCredit.expirationDate && (
                                    <Text
                                        is='p'
                                        fontSize='sm'
                                        children={`${localization.expires} ${getExpDateView(storeCredit.expirationDate)}`}
                                    />
                                )}
                            </Text>
                        </Grid>
                    </>
                ))}
                {giftCardPaymentGroups?.map((giftCard, index) => (
                    <GiftCardItem
                        giftCard={giftCard.paymentGroup}
                        hasDivider={hasNonGiftCardPaymentAdded || index !== 0}
                    />
                ))}
            </Box>

            {shouldShowAlternatePaymentButtons && (
                <AlternatePaymentButtons
                    isAfterpayEnabledForThisOrder={isAfterpayEnabledForThisOrder}
                    isKlarnaEnabledForThisOrder={isKlarnaEnabledForThisOrder}
                    isPayPalEnabled={isPayPalEnabled}
                    isPayPalPayLaterEligible={isPayPalPayLaterEligible}
                    isAfterpayEnabledForThisProfile={isAfterpayEnabledForThisProfile}
                    installmentValue={installmentValue}
                    localization={localization}
                    handleAfterPayExpand={handleAfterPayExpand}
                    handleKlarnaExpand={handleKlarnaExpand}
                    handlePayPalClick={handlePayPalClick}
                />
            )}
            {((!hasGiftCardInOrder && !isEfullfillmentOrder) || shouldRenderGiftCardSection) && (
                <>
                    <Divider
                        marginTop={[3, 4]}
                        color={colors.lightGray}
                    />
                    {giftCardPaymentGroups.length < 2 ? (
                        <UseAGiftCard />
                    ) : (
                        <Text
                            is='p'
                            children={localization.maxGiftCards}
                            color='gray'
                            fontSize='sm'
                            marginX={[4, 5]}
                            lineHeight='tight'
                            marginTop={[3, 4]}
                        />
                    )}
                </>
            )}
            {(shouldShowPazeGiftCardMessage || (hasGiftCardInOrder && !shouldRenderGiftCardSection)) && (
                <>
                    <Divider
                        marginTop={[3, 4]}
                        color={colors.lightGray}
                    />
                    <Text
                        is='p'
                        children={localization.giftCardsNotCombinable}
                        color='gray'
                        fontSize='sm'
                        marginX={[4, 5]}
                        lineHeight='tight'
                        marginTop={4}
                    />
                </>
            )}
        </>
    );
};

export default wrapFunctionalComponent(PaymentsDisplay, 'PaymentsDisplay');
